import {
  EnumTagEntityTypes,
  TagByIdQuery,
  useTagUpdateMutation,
} from "gql/generated";
import {
  getTagEntityTypesForTagGroup,
  tagInfoFormSchema,
  TagInfoFormValues,
} from "../../components";
import { getMultilingualString, updateMultilingual } from "shared/utils";
import { useCallback } from "react";
import { useFormHandler, usePostHog } from "shared/hooks";
import { InternalRefetchQueriesInclude } from "@apollo/client";

export type TagDetailsFormValues = {
  tag: TagByIdQuery["tagById"];
  onSuccess: (tag: TagByIdQuery["tagById"]) => void;
  onError: () => void;
  tagMutationCompleteRefreshesQueries?: InternalRefetchQueriesInclude;
};

export const useTagDetailsFlyoutForm = ({
  tag,
  onSuccess,
  onError,
  tagMutationCompleteRefreshesQueries,
}: TagDetailsFormValues) => {
  const defaultValues: TagInfoFormValues = {
    id: tag?._id ?? "",
    tagGroup: tag?.tagGroup?._id ?? "",
    name: getMultilingualString(tag?.name ?? {}),
    entityTypes: (tag?.entityTypes && tag?.entityTypes.length > 0
      ? tag.entityTypes
      : tag?.tagGroup
        ? getTagEntityTypesForTagGroup(tag.tagGroup.type, tag.tagGroup.key)
        : []) as EnumTagEntityTypes[],
    inheritable: tag?.inheritable ?? false,
  };

  const { trackPostHogException } = usePostHog();

  const [tagUpdateByIdMutation] = useTagUpdateMutation();

  return useFormHandler<TagInfoFormValues>(
    useCallback(
      async data => {
        if (
          !data ||
          !data.id ||
          !data.tagGroup ||
          !data.name ||
          !data.entityTypes ||
          data.entityTypes.length === 0
        ) {
          return;
        }

        try {
          const result = await tagUpdateByIdMutation({
            variables: {
              id: data.id,
              record: {
                tagGroup: data.tagGroup,
                name: updateMultilingual(tag?.name ?? {}, data.name),
                entityTypes: data.entityTypes as EnumTagEntityTypes[],
                inheritable: data.inheritable ?? false,
              },
            },
            ...(tagMutationCompleteRefreshesQueries
              ? { refetchQueries: tagMutationCompleteRefreshesQueries }
              : {}),
          });
          if (result.data?.tagUpdate?.record) {
            onSuccess(result.data.tagUpdate.record);
          } else {
            throw new Error(result.errors?.[0]?.message);
          }
        } catch (error) {
          trackPostHogException(error, {
            fileName: "useTagDetailsFlyoutForm.ts",
            method: "onSubmit",
            message: "Error updating tag",
          });
          onError();
        }
      },
      [
        tag,
        tagMutationCompleteRefreshesQueries,
        onError,
        onSuccess,
        tagUpdateByIdMutation,
        trackPostHogException,
      ],
    ),
    defaultValues,
    { schema: tagInfoFormSchema, mode: "onChange" },
  );
};
